import { ArrowLeft, BadgeCheck, Clock, NotebookPen, Pencil, Zap } from 'lucide-react';
import { useState, type CSSProperties, type MouseEvent, type ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { toAppError } from '../../api/errors';
import { useAuth } from '../../app/auth';
import { useShellExtras } from '../../app/shellExtras';
import { Routes } from '../../app/routes';
import {
  useDecide,
  useEmployees,
  useItem,
  useItemEvents,
  useMarkSeen,
  useRelated,
  useReportDone,
  useSetAutonomy,
} from '../../data/queries';
import type { DecisionKind, Item, ItemPayload } from '../../domain/models';
import { can, isEditable, isTask, isWorking, needsDecision } from '../../domain/models';
import { payloadToJson } from '../../domain/payload';
import { useLocale, useT } from '../../l10n/i18n';
import {
  errorMessage,
  headerTint,
  itemKindLabel,
  nameDative,
  statusStyle,
  timeAgo,
  toneColors,
  workKindIcon,
} from '../core/presentation';
import { AvatarStack, EmployeeAvatar } from '../core/widgets/EmployeeAvatar';
import { ErrorView, Pill, Skeleton } from '../core/widgets/common';
import { useToast } from '../core/widgets/toasts';
import { DecisionBar } from './DecisionBar';
import {
  CommentSheet,
  ConfirmAccessDialog,
  ConfirmDialog,
  ConfirmAdDialog,
  ConfirmPublishDialog,
  ConfirmSendDialog,
  HandoffSheet,
  type HandoffOutcome,
} from './dialogs';
import { LinkTile, TileList, safeUrl, useCopy } from './links';
import {
  accessResource,
  decidedMessage,
  documentAddressee,
  employeeNamed,
  handoffTargets,
  lastChangeRequest,
  recipient,
  requester,
  revisionNote,
} from './logic';
import { ChangeRequestCallout, ParentLink, RelatedList, StatusBanner, Timeline } from './parts';
import { ItemSection, PayloadView, takesLinks } from './PayloadView';
import './item.css';

// Contract shared by the item page and the inbox's side pane; the item screen fills it in.

export interface ItemViewProps {
  itemId: string;
  /** `page`: the item fills the page (`/items/:id`). `pane`: next to the inbox list on wide screens. */
  layout: 'page' | 'pane';
  /** The person is done with the item (decided it, handed it off, reported it done), so the caller
   * moves on: the page goes back, the pane opens the next item. Not called while approved manual work
   * still waits for "Paskelbta". */
  onFinished?: () => void;
}

/** One item: its content, its history, the person's decision, and the work handed on from it. Ported
 * from the mobile app's item_screen.dart. */
export function ItemView(props: ItemViewProps) {
  // Everything local (a draft, an open dialog) belongs to one item.
  return <ItemScreen key={props.itemId} {...props} />;
}

type Dialog =
  | { kind: 'reject' }
  | { kind: 'changes' }
  | { kind: 'published' }
  | { kind: 'handoff'; thenSeen: boolean }
  | { kind: 'confirmSend' }
  | { kind: 'confirmAccess' }
  | { kind: 'confirmAd'; campaign: string; change: string }
  | { kind: 'confirmPublish'; title: string }
  | { kind: 'letInAlways' };

function ItemScreen({ itemId, layout, onFinished }: ItemViewProps) {
  const itemQuery = useItem(itemId);
  const item = itemQuery.data;
  return (
    <div className={`iv iv-${layout}`} style={item ? ({ '--iv-tint': headerTint(item.employee.color) } as CSSProperties) : undefined}>
      {item ? (
        <LoadedItem item={item} layout={layout} onFinished={onFinished} />
      ) : (
        <div className="iv-scroll">
          {layout === 'page' && (
            <div className="iv-inner iv-topbar">
              <BackLink />
            </div>
          )}
          {itemQuery.isError ? (
            <ErrorView error={itemQuery.error} onRetry={() => void itemQuery.refetch()} />
          ) : (
            <div className="iv-inner iv-loading" aria-busy>
              <div className="iv-who">
                <Skeleton width={56} height={56} radius={19} />
                <Skeleton width={140} />
              </div>
              <Skeleton height={26} />
              <Skeleton width={220} height={26} />
              <Skeleton height={180} radius={22} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/** Back where the person came from, or to the inbox when they arrived by a link. A real link to the
 * inbox, so it also opens in a new tab. */
function BackLink() {
  const t = useT();
  const navigate = useNavigate();
  const location = useLocation();
  const hasHistory = location.key !== 'default';
  const back = (e: MouseEvent<HTMLAnchorElement>) => {
    if (!hasHistory || e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
    e.preventDefault();
    void navigate(-1);
  };
  return (
    <Link to={Routes.inbox} className="icon-btn iv-back" onClick={back} aria-label={t('actionBack')} title={t('actionBack')}>
      <ArrowLeft size={22} aria-hidden />
    </Link>
  );
}

function LoadedItem({ item, layout, onFinished }: { item: Item; layout: ItemViewProps['layout']; onFinished?: () => void }) {
  const t = useT();
  const locale = useLocale();
  const navigate = useNavigate();
  const toast = useToast();
  const copy = useCopy();
  const { session } = useAuth();
  const events = useItemEvents(item.id).data ?? [];
  const related = useRelated(item.id).data ?? [];
  const employees = useEmployees().data ?? [];
  const decide = useDecide();
  const reportDone = useReportDone();
  const markSeen = useMarkSeen();
  const setAutonomy = useSetAutonomy();
  const [draft, setDraft] = useState<{ etag?: string; payload: ItemPayload }>();
  const [dialog, setDialog] = useState<Dialog>();

  // A draft belongs to the version it was started from; a newer version replaces it.
  const editing = draft !== undefined && draft.etag === item.etag && isEditable(item);
  const payload = editing ? draft.payload : item.payload;
  const hasEdits = editing && JSON.stringify(payloadToJson(draft.payload)) !== JSON.stringify(payloadToJson(item.payload));
  const targets = handoffTargets(item, employees, related);
  const canHandOff = can(item, 'hand_off') && targets.length > 0;
  const isViewer = session?.role === 'viewer';
  const busy = decide.isPending || reportDone.isPending || markSeen.isPending;
  const changeRequest = item.revision > 1 && needsDecision(item) ? lastChangeRequest(events) : undefined;
  const status = statusStyle(t, item);
  const working = isWorking(item);
  const KindIcon = workKindIcon(item.kind);
  const message = item.payload.type === 'message_reply';
  // An owner may let this employee's requests in automatically from here (ADR 0011), when the
  // employee's autonomy offers it and it is not on yet. Not on a message the check found wrong: that is
  // no moment to trust the next ones.
  const author = employees.find((e) => e.id === item.employee.id);
  const offersLetIn =
    item.type === 'screening' &&
    needsDecision(item) &&
    (item.check === undefined || item.check.verdict === 'ok') &&
    author?.canSetAutonomy === true &&
    author.autonomy.some((k) => k.kind === 'request' && k.allowed && !k.automatic);

  const failed = (e: unknown) => toast(errorMessage(t, toAppError(e), item.employee.name));

  /** Says it went through and moves on, unless approved manual work now waits for this person. */
  const finish = (updated: Item | undefined, success: string) => {
    toast(success);
    if (updated && can(updated, 'report_result')) return;
    onFinished?.();
  };

  const runDecision = async (kind: DecisionKind, comment?: string) => {
    setDialog(undefined);
    const edited = kind === 'approve' && hasEdits && draft ? payloadToJson(draft.payload) : undefined;
    try {
      const updated = await decide.mutateAsync({ item, kind, comment, payload: edited });
      setDraft(undefined);
      finish(updated, decidedMessage(t, locale, item, kind));
    } catch (e) {
      failed(e);
    }
  };

  /** Approving something that leaves the organization asks first, naming who receives it, the money it
   * spends or what gets published. */
  const approve = () => {
    if (!item.outward) return void runDecision('approve');
    const p = payload;
    if (item.kind === 'access_request') setDialog({ kind: 'confirmAccess' });
    else if (p.type === 'ad_change') setDialog({ kind: 'confirmAd', campaign: p.campaign, change: p.change });
    else if (p.type === 'document' && !documentAddressee(item)) setDialog({ kind: 'confirmPublish', title: p.title });
    else setDialog({ kind: 'confirmSend' });
  };

  const markPublished = async (link: string) => {
    setDialog(undefined);
    try {
      const updated = await reportDone.mutateAsync({ itemId: item.id, link: link || undefined });
      finish(updated, message ? t('decidedMarkedSent') : t('decidedPublished'));
    } catch (e) {
      failed(e);
    }
  };

  const handedOff = async (o: HandoffOutcome, thenSeen: boolean) => {
    setDialog(undefined);
    if ('existingItemId' in o) {
      // One open task per item and employee: show the one already going.
      toast(t('handoffAlready', { name: o.employee.name }));
      void navigate(Routes.item(o.existingItemId));
      return;
    }
    const done = t('handoffDone', { name: nameDative(locale, o.employee.name) });
    const task = o.task;
    if (thenSeen) {
      // Handing the work on is also having read it.
      await markSeen.mutateAsync([item]).catch(() => undefined);
      onFinished?.();
    }
    toast(done, { label: t('actionOpen'), run: () => void navigate(Routes.item(task.id)) });
  };

  const letInAlways = async () => {
    setDialog(undefined);
    try {
      await setAutonomy.mutateAsync({ employeeId: item.employee.id, kind: 'request', automatic: true });
      toast(t('autonomyTurnedOnRequest'));
    } catch (e) {
      failed(e);
    }
  };

  const links = takesLinks(item.payload) ? [] : linkTiles(item);

  return (
    <>
      <div className="iv-scroll">
        <header className="iv-header">
          <div className="iv-inner">
            <div className="iv-topbar">
              {layout === 'page' && <BackLink />}
              <span className="iv-grow" />
              {canHandOff && !editing && (
                <button type="button" className="iv-handoff-action" onClick={() => setDialog({ kind: 'handoff', thenSeen: false })}>
                  {targets.length === 1 ? (
                    <EmployeeAvatar avatar={targets[0]!.avatar} color={targets[0]!.color} size={26} />
                  ) : (
                    <AvatarStack size={24} people={targets.slice(0, 3)} />
                  )}
                  <span className="label-medium">{t('actionHandOff')}</span>
                </button>
              )}
              {isEditable(item) && !editing && (
                <button
                  type="button"
                  className="btn btn-outlined btn-small iv-edit-action"
                  onClick={() => setDraft({ etag: item.etag, payload: item.payload })}
                >
                  <Pencil size={16} aria-hidden />
                  {t('actionEdit')}
                </button>
              )}
            </div>
            <div className="iv-who">
              <EmployeeAvatar avatar={item.employee.avatar} color={item.employee.color} size={56} animate label={item.employee.name} />
              <div className="iv-grow iv-min0">
                <p className="title-medium">{item.employee.name}</p>
                <p className="body-small">{item.employee.title}</p>
              </div>
              <Pill label={item.workspace.name} dot={item.workspace.color} color="var(--ink)" background="var(--surface)" />
            </div>
            <h1 className="headline-medium iv-title">{item.title}</h1>
            <div className="iv-pills">
              <Pill label={itemKindLabel(t, item)} icon={KindIcon} background="var(--surface)" />
              <Pill label={status.label} tone={status.tone} dot={working ? toneColors(status.tone).fg : undefined} pulse={working} />
              {item.revision > 1 && <Pill label={t('revisedBadge')} tone="accent" />}
              {item.priority === 'high' && needsDecision(item) && <Pill label={t('priorityHigh')} icon={Zap} tone="danger" />}
              <span title={item.updatedAt.toLocaleString(locale)}>
                <Pill label={timeAgo(t, item.updatedAt)} icon={Clock} background="var(--surface)" />
              </span>
            </div>
          </div>
        </header>

        <div className="iv-inner iv-body">
          {item.parentId && !isTask(item) && <ParentLink parentId={item.parentId} />}
          {/* A task's summary is the person's note, shown with the task. */}
          {item.summary && item.type !== 'screening' && !isTask(item) && (
            <p className="body-large muted iv-gap-xl pre-wrap">{item.summary}</p>
          )}
          {changeRequest && (
            <div className="iv-gap-xl">
              <ChangeRequestCallout comment={changeRequest} note={revisionNote(events)} employee={item.employee.name} />
            </div>
          )}
          <StatusBanner item={item} />
          {related.length > 0 && (
            <ItemSection title={isTask(item) ? t('sectionResults') : t('sectionHandedOff')}>
              <RelatedList related={related} />
            </ItemSection>
          )}
          {links.length > 0 && (
            <ItemSection title={t('sectionLinks')}>
              <TileList>{links}</TileList>
            </ItemSection>
          )}
          {editing && (
            <p className="body-small iv-editing-hint">
              <NotebookPen size={20} aria-hidden />
              {t('editingHint')}
            </p>
          )}
          <PayloadView
            payload={payload}
            workspaceName={item.workspace.name}
            onChange={editing ? (p) => setDraft({ etag: item.etag, payload: p }) : undefined}
            letIn={
              item.status === 'approved' || item.status === 'completed'
                ? item.decision?.automatic
                  ? 'automatic'
                  : true
                : false
            }
            check={item.check}
            checkAction={
              offersLetIn ? (
                <button
                  type="button"
                  className="btn btn-small iv-letin-always"
                  disabled={setAutonomy.isPending}
                  onClick={() => setDialog({ kind: 'letInAlways' })}
                >
                  <BadgeCheck size={16} aria-hidden />
                  {t('autonomyAlwaysLetIn')}
                </button>
              ) : undefined
            }
            employeeNamed={(name) => employeeNamed(employees, item.workspace.id, name)}
            links={item.links}
            manual={item.execution === 'manual'}
            awaitsManual={can(item, 'report_result')}
          />
          {events.length > 0 && (
            <ItemSection title={t('sectionTimeline')}>
              <Timeline events={events} />
            </ItemSection>
          )}
        </div>
      </div>

      <DecisionBar
        item={item}
        editing={editing}
        handoffTargets={canHandOff ? targets : []}
        isViewer={isViewer}
        busy={busy}
        on={{
          approve,
          reject: () => setDialog({ kind: 'reject' }),
          spam: () => void runDecision('reject'),
          requestChanges: () => setDialog({ kind: 'changes' }),
          discardEdits: () => setDraft(undefined),
          handOffThenSeen: () => setDialog({ kind: 'handoff', thenSeen: true }),
          copyForPaste: (text, done) => copy(text, t('copiedPasteIt', { done })),
          published: () => setDialog({ kind: 'published' }),
        }}
      />

      {dialog && (
        <Dialogs
          dialog={dialog}
          item={item}
          targets={targets}
          close={() => setDialog(undefined)}
          decide={(kind, comment) => void runDecision(kind, comment)}
          published={(link) => void markPublished(link)}
          handedOff={(o, thenSeen) => void handedOff(o, thenSeen)}
          letInAlways={() => void letInAlways()}
        />
      )}
    </>
  );
}

function Dialogs({
  dialog,
  item,
  targets,
  close,
  decide,
  published,
  handedOff,
  letInAlways,
}: {
  dialog: Dialog;
  item: Item;
  targets: ReturnType<typeof handoffTargets>;
  close: () => void;
  decide: (kind: DecisionKind, comment?: string) => void;
  published: (link: string) => void;
  handedOff: (o: HandoffOutcome, thenSeen: boolean) => void;
  letInAlways: () => void;
}): ReactNode {
  const t = useT();
  const locale = useLocale();
  const { suggestChange } = useShellExtras();
  const employee = item.employee.name;
  const message = item.payload.type === 'message_reply';
  switch (dialog.kind) {
    case 'reject':
      return (
        <CommentSheet
          title={t('rejectTitle')}
          hint={t('rejectHint')}
          submitLabel={t('actionReject')}
          danger
          onClose={close}
          onSubmit={(reason) => decide('reject', reason)}
        />
      );
    case 'changes':
      return (
        <CommentSheet
          title={t('changesTitle')}
          hint={t('changesHint', { name: nameDative(locale, employee) })}
          submitLabel={t('changesSend', { name: nameDative(locale, employee) })}
          required
          suggestions={[t('changesShorter'), t('changesWarmer'), t('changesFormal'), t('changesAddLink')]}
          initialText={suggestChange?.(item)}
          onClose={close}
          onSubmit={(comment) => decide('request_changes', comment)}
        />
      );
    case 'published':
      return (
        <CommentSheet
          title={message ? t('sentTitle') : t('publishedTitle')}
          hint={message ? t('sentLinkHint') : t('publishedLinkHint')}
          submitLabel={message ? t('sentConfirm') : t('publishedConfirm')}
          singleLine
          inputType="url"
          onClose={close}
          onSubmit={published}
        />
      );
    case 'handoff':
      return (
        <HandoffSheet
          itemId={item.id}
          targets={targets}
          onClose={close}
          onDone={(o) => handedOff(o, dialog.thenSeen)}
        />
      );
    case 'confirmSend':
      return <ConfirmSendDialog client={recipient(t, item)} onClose={close} onConfirm={() => decide('approve')} />;
    case 'confirmAccess':
      return (
        <ConfirmAccessDialog
          name={requester(item)}
          resource={accessResource(item)}
          onClose={close}
          onConfirm={() => decide('approve')}
        />
      );
    case 'confirmAd':
      return (
        <ConfirmAdDialog campaign={dialog.campaign} change={dialog.change} onClose={close} onConfirm={() => decide('approve')} />
      );
    case 'confirmPublish':
      return <ConfirmPublishDialog title={dialog.title} onClose={close} onConfirm={() => decide('approve')} />;
    case 'letInAlways':
      return (
        <ConfirmDialog
          icon={BadgeCheck}
          tone="warning"
          title={t('autonomyConfirmRequestTitle')}
          body={t('autonomyConfirmRequestBody')}
          confirmLabel={t('autonomyConfirmAction')}
          onConfirm={letInAlways}
          onClose={close}
        />
      );
  }
}

/** The item's links as tiles, without the address a deliverable already opens. */
function linkTiles(item: Item): ReactNode[] {
  const shown = item.payload.type === 'deliverable' ? safeUrl(item.payload.url)?.href : undefined;
  return item.links.flatMap((l, i) => {
    const u = safeUrl(l.url);
    return u && u.href !== shown ? [<LinkTile key={i} label={l.label} url={u} />] : [];
  });
}
